import React, { useEffect } from 'react';
import classnames from 'classnames';
import { history, useSelector, useDispatch } from 'umi';
import { Layout, Tabs, TabPanel } from 'tea-component';
import Loading from '@gp/components/Loading';
import PageAccess from '@gp/components/PageAccess';
import Overview from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Overview';
import Calendar from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Calendar';
import Projects from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Projects';
import Finance from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Finance';
import People from '@gp/pages/StudioDashboard/StudioOverall/Tabs/People';
import Management from '@gp/pages/StudioDashboard/StudioOverall/Tabs/Management';
import useAuthChecker from '@gp/pages/StudioDashboard/StudioOverall/hooks/useAuthChecker';
import { context as Context } from '@gp/components/Context';
import { reportOperationLog } from '@gp/services/global';
import { getLocationIsValid } from '@gp/utils/common';
import { getMatchedTabId } from './utils';
import {
  STUDIO_OVERALL_TAB_ID_MODEL_NAMESPACE,
  IStudioOverallTabState,
} from '@gp/models/studioOverallTabId';
import { SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE } from '@gp/constants/classNames';
import { SLASH_G_POP } from '@gp/constants/const';
import { DESCRIPTION_C } from '@gp/constants/noPermissionTips';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import {
  STUDIO_OVERALL_PAGE_LOG_KEY_MAP,
  STUDIO_OVERALL_PAGE_CLASSNAME,
  STUDIO_OVERALL_PAGE_BODY_CLASSNAME,
} from '@gp/pages/StudioDashboard/StudioOverall/constants';

import type { IRecord, ITab } from '@gp/types/global';

import '@gp/pages/StudioDashboard/StudioOverall/scss/index.scss';

const { Content } = Layout;

const TABS: ITab[] = [
  {
    id: 'overview',
    label: 'Overview',
    component: <Overview />,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    component: <Calendar />,
  },
  {
    id: 'projects',
    label: 'Projects',
    component: <Projects />,
  },
  {
    id: 'finance',
    label: 'Finance',
    component: <Finance />,
  },
  {
    id: 'people',
    label: 'People',
    component: <People />,
  },
  {
    id: 'management_boardGovernance',
    label: 'Management',
    component: <Management />,
  },
];

const StudioOverall: React.FC<IStudioOverallProps> = () => {
  const filterObj: IRecord = getLocationIsValid('/Studio/PortfolioOverview');

  const dispatch = useDispatch();

  const { currentTabId } = useSelector<any, IStudioOverallTabState>(
    (state) => state?.[STUDIO_OVERALL_TAB_ID_MODEL_NAMESPACE] || {},
  );

  const setCurrentTabId = (payload?: string) => {
    dispatch({
      type: `${STUDIO_OVERALL_TAB_ID_MODEL_NAMESPACE}/updateCurrentTabId`,
      payload,
    });
  };

  const { isLoading: isAccessLoading, resOriginalData: accessData } = useAuthChecker({
    studioPageKey: 'studio_overall',
    studioId: '',
  });

  const tab = TABS.find((item) => item?.id === currentTabId) as ITab;

  useEffect(() => {
    if (!currentTabId || !STUDIO_OVERALL_PAGE_LOG_KEY_MAP[currentTabId]) return;
    // @ts-ignore
    reportOperationLog(ALL_REPORT_OPERATION_LOG[STUDIO_OVERALL_PAGE_LOG_KEY_MAP[currentTabId]]);
  }, [currentTabId]);

  useEffect(() => {
    const activeId = getMatchedTabId(TABS, filterObj);
    setCurrentTabId(activeId);
  }, []);

  return (
    <Context.Provider value={{ accessData }}>
      <Content className={classnames('dbg-page dbg-tab-page', STUDIO_OVERALL_PAGE_CLASSNAME)}>
        <Content.Header
          className="contentHead-normal dbg-tab-page__header"
          title="Portfolio Overview"
        >
          <Tabs
            className="dbg-tab-page__tabs"
            ceiling
            tabs={TABS}
            activeId={currentTabId}
            onActive={(tab) => {
              document.querySelector(`.${STUDIO_OVERALL_PAGE_BODY_CLASSNAME}`)?.scrollTo(0, 0);
              const filter = { ...filterObj };
              filter.tab = tab?.id;
              // 切换tab页签时，删除Projects页签保存的segment的value
              delete filter.segment;
              const newFilter = encodeURIComponent(JSON.stringify(filter));
              history.replace(`${SLASH_G_POP}/Studio/PortfolioOverview?filter=${newFilter}`);
              setCurrentTabId(tab?.id);
            }}
          />
        </Content.Header>
        <Content.Body
          full
          className={classnames(
            'dbg-tab-page__body',
            STUDIO_OVERALL_PAGE_BODY_CLASSNAME,
            SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE,
          )}
        >
          {isAccessLoading && <Loading style={{ height: 200 }} loading />}
          {!isAccessLoading && tab && (
            <TabPanel key={tab?.id} id={tab?.id}>
              <PageAccess
                className="dbg-studio-overall__page-access"
                isAccess={!!accessData?.[tab?.id]}
                description={DESCRIPTION_C}
              >
                {tab?.component}
              </PageAccess>
            </TabPanel>
          )}
        </Content.Body>
      </Content>
    </Context.Provider>
  );
};

export default React.memo(StudioOverall);
